import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import type {
  AgentChatSessionCampaignCreateResponse,
  AgentChatSessionListResponse,
  AgentSessionHistoryMessageResponse,
  AgentChatSessionResponse,
  AgentChatSessionShareResponse,
  AgentSessionHistoryResponse,
  AgentSessionHistoryThinkingStepResponse,
  CampaignCreateRequest,
  CampaignAgentMode,
  PublicAgentChatSessionShareResponse,
} from "@/lib/api/generated";
import {
  createAgentChatSessionApiWorkspacesWorkspaceIdAgentSessionsPost,
  createAgentChatSessionCampaignApiWorkspacesWorkspaceIdAgentSessionsSessionIdCampaignsPost,
  getAgentChatSessionApiWorkspacesWorkspaceIdAgentSessionsSessionIdGet,
  listAgentChatSessionsApiWorkspacesWorkspaceIdAgentSessionsGet,
} from "@/lib/api/generated/agent-sessions/agent-sessions";
import { extractApiErrorMessage } from "@/lib/api/error";
import { customInstance } from "@/lib/api/custom-instance";
import { logError, logInfo } from "@/lib/structured-log";

const workspaceIdSchema = z.object({
  workspaceId: z.string().min(1),
});

type SerializableProgressData = Record<string, NonNullable<unknown>>;

type SerializableThinkingStep = Omit<AgentSessionHistoryThinkingStepResponse, "progress_data"> & {
  progress_data?: SerializableProgressData | null;
};

type SerializableHistoryMessage = Omit<AgentSessionHistoryMessageResponse, "thinking_steps"> & {
  thinking_steps?: SerializableThinkingStep[];
};

export interface SerializableAgentSessionHistoryResponse
  extends Omit<AgentSessionHistoryResponse, "messages"> {
  messages: SerializableHistoryMessage[];
}

export interface SerializablePublicAgentChatSessionShareResponse
  extends Omit<PublicAgentChatSessionShareResponse, "history"> {
  history: SerializableAgentSessionHistoryResponse;
}

const listAgentSessionsSchema = workspaceIdSchema.extend({
  limit: z.number().int().min(1).max(50).optional(),
});

const createAgentSessionSchema = workspaceIdSchema.extend({
  agentMode: z.enum(["sales", "sourcing", "website"]),
  title: z.string().optional(),
});

const getAgentSessionSchema = workspaceIdSchema.extend({
  sessionId: z.string().min(1),
});

const updateAgentSessionSchema = getAgentSessionSchema.extend({
  title: z.string().trim().min(1).max(255),
});

const createSessionCampaignSchema = getAgentSessionSchema.extend({
  campaign: z.custom<CampaignCreateRequest>(),
});

export const listAgentChatSessionsServer = createServerFn({ method: "GET" })
  .validator(listAgentSessionsSchema)
  .handler(async ({ data }): Promise<AgentChatSessionListResponse> => {
    const response = await listAgentChatSessionsApiWorkspacesWorkspaceIdAgentSessionsGet(
      data.workspaceId,
      { limit: data.limit ?? 20 },
    );

    if (response.status !== 200) {
      const message = extractApiErrorMessage(response, "Failed to list agent chat sessions");
      logError("agent_sessions.list.failed", {
        workspace_id: data.workspaceId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return response.data;
  });

export const createAgentChatSessionServer = createServerFn({ method: "POST" })
  .validator(createAgentSessionSchema)
  .handler(async ({ data }): Promise<AgentChatSessionResponse> => {
    const agentMode = data.agentMode as CampaignAgentMode;
    const response = await createAgentChatSessionApiWorkspacesWorkspaceIdAgentSessionsPost(
      data.workspaceId,
      {
        agent_mode: agentMode,
        title: data.title,
      },
    );

    if (response.status !== 201) {
      const message = extractApiErrorMessage(response, "Failed to create agent chat session");
      logError("agent_sessions.create.failed", {
        workspace_id: data.workspaceId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    logInfo("agent_sessions.create.success", {
      workspace_id: data.workspaceId,
      session_id: response.data.id,
      agent_mode: response.data.agent_mode,
    });

    return response.data;
  });

export const getAgentChatSessionServer = createServerFn({ method: "GET" })
  .validator(getAgentSessionSchema)
  .handler(async ({ data }): Promise<AgentChatSessionResponse> => {
    const response = await getAgentChatSessionApiWorkspacesWorkspaceIdAgentSessionsSessionIdGet(
      data.workspaceId,
      data.sessionId,
    );

    if (response.status !== 200) {
      const message = extractApiErrorMessage(response, "Failed to get agent chat session");
      logError("agent_sessions.get.failed", {
        workspace_id: data.workspaceId,
        session_id: data.sessionId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return response.data;
  });

export const updateAgentChatSessionServer = createServerFn({ method: "POST" })
  .validator(updateAgentSessionSchema)
  .handler(async ({ data }): Promise<AgentChatSessionResponse> => {
    const response = await customInstance<{
      data: AgentChatSessionResponse;
      status: number;
      headers: Headers;
    }>(`/api/workspaces/${data.workspaceId}/agent-sessions/${data.sessionId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title: data.title }),
    });

    if (response.status !== 200) {
      const message = extractApiErrorMessage(response, "Failed to update agent chat session");
      logError("agent_sessions.update.failed", {
        workspace_id: data.workspaceId,
        session_id: data.sessionId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return response.data;
  });

export const getAgentChatSessionHistoryServer = createServerFn({ method: "GET" })
  .validator(getAgentSessionSchema)
  .handler(async ({ data }): Promise<SerializableAgentSessionHistoryResponse> => {
    const response = await customInstance<{
      data: AgentSessionHistoryResponse;
      status: number;
      headers: Headers;
    }>(`/api/workspaces/${data.workspaceId}/agent-sessions/${data.sessionId}/history`, {
      method: "GET",
    });

    if (response.status !== 200) {
      const message = extractApiErrorMessage(response, "Failed to get agent chat session history");
      logError("agent_sessions.history.get.failed", {
        workspace_id: data.workspaceId,
        session_id: data.sessionId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return toSerializableHistory(response.data);
  });

export const enableAgentChatSessionShareServer = createServerFn({ method: "POST" })
  .validator(getAgentSessionSchema)
  .handler(async ({ data }): Promise<AgentChatSessionShareResponse> => {
    const response = await customInstance<{
      data: AgentChatSessionShareResponse;
      status: number;
      headers: Headers;
    }>(`/api/workspaces/${data.workspaceId}/agent-sessions/${data.sessionId}/share`, {
      method: "POST",
    });

    if (response.status !== 200) {
      const message = extractApiErrorMessage(response, "Failed to create share link");
      logError("agent_sessions.share.create.failed", {
        workspace_id: data.workspaceId,
        session_id: data.sessionId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return response.data;
  });

export const disableAgentChatSessionShareServer = createServerFn({ method: "POST" })
  .validator(getAgentSessionSchema)
  .handler(async ({ data }): Promise<AgentChatSessionResponse> => {
    const response = await customInstance<{
      data: AgentChatSessionResponse;
      status: number;
      headers: Headers;
    }>(`/api/workspaces/${data.workspaceId}/agent-sessions/${data.sessionId}/share`, {
      method: "DELETE",
    });

    if (response.status !== 200) {
      const message = extractApiErrorMessage(response, "Failed to disable share link");
      logError("agent_sessions.share.disable.failed", {
        workspace_id: data.workspaceId,
        session_id: data.sessionId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return response.data;
  });

const publicShareSchema = z.object({
  shareToken: z.string().min(20).max(128),
});

export const getPublicAgentChatSessionShareServer = createServerFn({ method: "GET" })
  .validator(publicShareSchema)
  .handler(async ({ data }): Promise<SerializablePublicAgentChatSessionShareResponse> => {
    const response = await customInstance<{
      data: PublicAgentChatSessionShareResponse;
      status: number;
      headers: Headers;
    }>(`/api/public/agent-sessions/${data.shareToken}`, {
      method: "GET",
    });

    if (response.status !== 200) {
      const message = extractApiErrorMessage(response, "Shared task session not found");
      logError("agent_sessions.share.public_get.failed", {
        share_token_prefix: data.shareToken.slice(0, 8),
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return {
      ...response.data,
      history: toSerializableHistory(response.data.history),
    };
  });

export const createAgentChatSessionCampaignServer = createServerFn({ method: "POST" })
  .validator(createSessionCampaignSchema)
  .handler(async ({ data }): Promise<AgentChatSessionCampaignCreateResponse> => {
    const response =
      await createAgentChatSessionCampaignApiWorkspacesWorkspaceIdAgentSessionsSessionIdCampaignsPost(
        data.workspaceId,
        data.sessionId,
        {
          campaign: data.campaign,
        },
      );

    if (response.status !== 201) {
      const message = extractApiErrorMessage(response, "Failed to create session campaign task");
      logError("agent_sessions.campaign.create.failed", {
        workspace_id: data.workspaceId,
        session_id: data.sessionId,
        status: response.status,
        error: message,
      });
      throw new Error(message);
    }

    return response.data;
  });

function toSerializableHistory(
  history: AgentSessionHistoryResponse,
): SerializableAgentSessionHistoryResponse {
  return {
    ...history,
    messages: history.messages.map((message) => ({
      ...message,
      thinking_steps: message.thinking_steps?.map((step) => ({
        ...step,
        progress_data: toSerializableProgressData(step.progress_data),
      })),
    })),
  };
}

function toSerializableProgressData(
  value: AgentSessionHistoryThinkingStepResponse["progress_data"],
): SerializableProgressData | null | undefined {
  if (value === undefined || value === null) {
    return value;
  }
  if (typeof value !== "object" || Array.isArray(value)) {
    return {};
  }
  return value as SerializableProgressData;
}
